import type {
  OrderProductDto,
  OrderResponse,
  ProductDto,
  ProductVariantDto,
} from 'api/generated/types.gen';

export type ProductCategory = {
  /** The category's name, from the products' `groupingText`. */
  name: string;
  products: ProductDto[];
};

const byPosition = (a: { position?: number }, b: { position?: number }) =>
  (a.position ?? 0) - (b.position ?? 0);

/**
 * Groups products into categories by `groupingText`. Categories keep the order
 * of their first product by `position`, and products are sorted by `position`
 * within each one. Products without a category share an unnamed one.
 */
export const groupProductsByCategory = (
  products: ProductDto[]
): ProductCategory[] => {
  const categories = new Map<string, ProductDto[]>();
  [...products].sort(byPosition).forEach((product) => {
    const name = product.groupingText?.trim() ?? '';
    const list = categories.get(name);
    if (list) list.push(product);
    else categories.set(name, [product]);
  });
  return [...categories].map(([name, list]) => ({ name, products: list }));
};

/** Whether nothing of the product can be bought any more. */
export const isProductSoldOut = (product: ProductDto): boolean =>
  product.saleStatus === 'SoldOut' ||
  (product.variants ?? []).every(
    (variant) => !variant.available || variant.saleStatus === 'SoldOut'
  );

/** Whether the product's variants differ in price, shown as "From {min}". */
export const hasPriceRange = (product: ProductDto): boolean =>
  (product.minPrice ?? 0) < (product.maxPrice ?? 0);

/**
 * Whether the buyer has to choose options before the product can be added. A
 * product without variations still carries a single variant, which is what
 * gets added.
 */
export const requiresVariantChoice = (product: ProductDto): boolean =>
  (product.variations ?? []).length > 0;

/** The variations of a product in display order, with their options sorted. */
export const getSortedVariations = (product: ProductDto) =>
  [...(product.variations ?? [])].sort(byPosition).map((variation) => ({
    ...variation,
    options: [...(variation.options ?? [])].sort(byPosition),
  }));

const isVariantBuyable = (variant: ProductVariantDto) =>
  variant.available !== false && variant.saleStatus !== 'SoldOut';

/**
 * The variant matching the chosen option of every variation, or `undefined`
 * while a choice is missing. `selection` maps a variation id to the chosen
 * option id. A product without variations resolves to its single variant.
 */
export const resolveVariant = (
  product: ProductDto,
  selection: Record<number, number | undefined>
): ProductVariantDto | undefined => {
  const variations = product.variations ?? [];
  if (variations.length === 0) return product.variants?.[0];

  const chosen = variations.map(
    (variation) => selection[variation.productVariationId ?? -1]
  );
  if (chosen.some((optionId) => optionId == null)) return undefined;

  return (product.variants ?? []).find((variant) => {
    const combination = variant.variationCombination ?? [];
    return (
      combination.length === chosen.length &&
      chosen.every((optionId) => combination.includes(optionId!))
    );
  });
};

/** Whether some buyable variant contains every one of the options. */
const isCombinationBuyable = (product: ProductDto, optionIds: number[]) =>
  (product.variants ?? []).some((variant) => {
    if (!isVariantBuyable(variant)) return false;
    const combination = variant.variationCombination ?? [];
    return optionIds.every((optionId) => combination.includes(optionId));
  });

/**
 * Whether an option can be picked: it is available itself and some buyable
 * variant contains it. The choices made for other variations don't narrow
 * this, so every buyable combination stays reachable; picking an option clears
 * the choices it can't be bought with instead (see `chooseOption`).
 */
export const isOptionSelectable = (
  product: ProductDto,
  variationId: number,
  optionId: number
): boolean => {
  const option = (product.variations ?? [])
    .find((variation) => variation.productVariationId === variationId)
    ?.options?.find((o) => o.productVariationOptionId === optionId);
  if (!option || option.available === false) return false;
  return isCombinationBuyable(product, [optionId]);
};

/**
 * The selection after picking an option. The choices for the other variations
 * are kept, in display order, as long as some buyable variant still contains
 * them together with the pick and the choices kept before them; the rest are
 * cleared, to be chosen again.
 */
export const chooseOption = (
  product: ProductDto,
  selection: Record<number, number | undefined>,
  variationId: number,
  optionId: number
): Record<number, number | undefined> => {
  const next: Record<number, number | undefined> = { [variationId]: optionId };
  const kept = [optionId];
  getSortedVariations(product).forEach((variation) => {
    const id = variation.productVariationId;
    if (id == null || id === variationId) return;
    const choice = selection[id];
    if (choice == null || !isCombinationBuyable(product, [...kept, choice])) {
      return;
    }
    kept.push(choice);
    next[id] = choice;
  });
  return next;
};

/**
 * The options a variant stands for, as one "Variation: Option" label per
 * variation in display order. A product without variations has none.
 */
export const getVariantOptionLabels = (
  product: ProductDto,
  variant: ProductVariantDto
): string[] => {
  const combination = variant.variationCombination ?? [];
  return getSortedVariations(product).flatMap((variation) => {
    const option = variation.options.find((o) =>
      combination.includes(o.productVariationOptionId ?? -1)
    );
    return option ? [`${variation.name}: ${option.value}`] : [];
  });
};

/** Whether a resolved variant can be added to the cart. */
export const canAddVariant = (
  variant: ProductVariantDto | undefined
): variant is ProductVariantDto =>
  !!variant && variant.productVariantId != null && isVariantBuyable(variant);

/** One line per product variant of an event in the order. */
export type OrderProductLine = {
  productVariantId: number;
  productId: number;
  /** The line's units, one per order row, oldest first. */
  encryptedOrderProductIds: string[];
  name: string;
  /** The product's category. */
  groupingText?: string;
  /** The chosen options, as "Variation: Option / Variation: Option". */
  variantName?: string;
  imageUrl?: string;
  quantity: number;
  priceWithTax: number;
  /** The combined price before discounts, set when any unit was discounted. */
  originalPrice?: number;
};

/**
 * The products the order holds for an event, with each variant's units merged
 * into one line carrying the quantity and their combined price.
 */
export const getEventProductLines = (
  order: OrderResponse | undefined,
  eventId: number | undefined
): OrderProductLine[] => {
  const lines = new Map<number, OrderProductLine>();
  (order?.products ?? [])
    .filter((product: OrderProductDto) => product.eventId === eventId)
    .forEach((product) => {
      const variantId = product.productVariantId ?? 0;
      const line = lines.get(variantId);
      const price = product.priceWithTax ?? 0;
      const original = product.originalPrice ?? undefined;
      if (line) {
        if (original != null || line.originalPrice != null) {
          line.originalPrice =
            (line.originalPrice ?? line.priceWithTax) + (original ?? price);
        }
        line.quantity += 1;
        line.priceWithTax += price;
        if (product.encryptedOrderProductId) {
          line.encryptedOrderProductIds.push(product.encryptedOrderProductId);
        }
        return;
      }
      lines.set(variantId, {
        productVariantId: variantId,
        productId: product.productId ?? 0,
        encryptedOrderProductIds: product.encryptedOrderProductId
          ? [product.encryptedOrderProductId]
          : [],
        name: product.name ?? '',
        groupingText: product.groupingText?.trim() || undefined,
        variantName: product.variantName || undefined,
        imageUrl: product.imageUrl ?? undefined,
        quantity: 1,
        priceWithTax: price,
        originalPrice: original,
      });
    });
  return [...lines.values()];
};

/**
 * The most units an order line can hold: its own units plus what is left of
 * the variant, whose remaining count already leaves this order's units out.
 * `null` when unlimited or while the offered products are loading; the line's
 * own quantity once the variant can no longer be bought.
 */
export const getProductLineMaximum = (
  line: OrderProductLine,
  offered: ProductDto[] | undefined
): number | null => {
  if (!offered) return null;
  const variant = offered
    .flatMap((product) => product.variants ?? [])
    .find((v) => v.productVariantId === line.productVariantId);
  if (!variant || !isVariantBuyable(variant)) return line.quantity;
  return variant.remaining == null
    ? null
    : line.quantity + Math.max(0, variant.remaining);
};

/**
 * The line holding `quantity` units, its prices scaled by the average unit
 * price. Stands in for the line while a quantity change is on its way.
 */
export const withQuantity = (
  line: OrderProductLine,
  quantity: number
): OrderProductLine => {
  if (quantity === line.quantity || line.quantity === 0) return line;
  const scale = quantity / line.quantity;
  return {
    ...line,
    quantity,
    priceWithTax: line.priceWithTax * scale,
    originalPrice:
      line.originalPrice == null ? undefined : line.originalPrice * scale,
  };
};
